import React from 'react';
import { clsx } from 'clsx';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';

const COLOR_RESOLVED = '#5D4EC7';
const COLOR_ESCALATED = '#E1DCFF';
const COLOR_SUCCESS = '#12823B';
const COLOR_ERROR = '#E52020';
const COLOR_AXIS = '#8585A0';

type TotalProcessedTicketsCardProps = {
  total?: number | string;
  totalTrend?: number | null;
  resolved?: number;
  resolvedTrend?: number | null;
  escalated?: number;
  escalatedTrend?: number | null;
  yTicks?: number[];
  tooltipDescription?: React.ReactNode;
  className?: string;
  timeSeriesResponse: any;
  icon?: null | React.ReactNode;
  showHeaderStats?: boolean;
  showLegend?: boolean;
};

const InfoIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    className={'info-tooltip-icon'}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 18.333A8.333 8.333 0 1 0 10 1.667a8.333 8.333 0 0 0 0 16.666Zm0-1.666a6.667 6.667 0 1 0 0-13.334 6.667 6.667 0 0 0 0 13.334Zm.833-10a.833.833 0 1 1-1.666 0 .833.833 0 0 1 1.666 0ZM9.167 9.167h1.666v5h-1.666v-5Z"
      fill="#8585A0"
    />
  </svg>
);

const TrendBadge: React.FC<{ value: number }> = ({ value }) => {
  const up = value > 0;
  const color = up ? COLOR_SUCCESS : COLOR_ERROR;
  return (
    <div className="flex items-center gap-0.5">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="17"
        height="16"
        viewBox="0 0 17 16"
        fill="none"
        aria-hidden="true"
      >
        {up ? (
          <path d="M8.5 4L12.3971 10.75H4.60289L8.5 4Z" fill={color} />
        ) : (
          <path d="M8.5 12L4.60289 5.25H12.3971L8.5 12Z" fill={color} />
        )}
      </svg>
      <span className="text-xs font-medium leading-[1.5]" style={{ color }}>
        {Math.abs(value)}%
      </span>
    </div>
  );
};

const formatNumber = (n: number) =>
  n >= 10000 ? new Intl.NumberFormat('en-US').format(n).replace(/,/g, ' ') : String(n);

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-sm-5 shadow-tooltip text-xs py-2.5 px-3 flex flex-col gap-1.5 items-start font-jamjuree">
      <p className="font-medium text-xs opacity-30">{label}</p>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 aspect-square" style={{ backgroundColor: p.color }} />
          <p className="font-medium">{p.name} -</p>
          <p className="font-bold text-sm">{p.value}</p>
        </div>
      ))}
    </div>
  );
};

export const TotalProcessedTicketsCard: React.FC<TotalProcessedTicketsCardProps> = ({
  total = 0,
  totalTrend = null,
  resolved = 0,
  resolvedTrend = null,
  escalated = 0,
  escalatedTrend = null,
  tooltipDescription,
  className,
  timeSeriesResponse,
  icon,
  showHeaderStats = true,
  showLegend = false,
}) => {
  return (
    <div
      className={clsx(
        'flex flex-col items-start gap-4 p-4 w-full bg-white border border-[#F3F4F7] rounded-sm-5 font-jamjuree',
        className,
      )}
    >
      <div className="flex flex-col gap-1 items-start w-full">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium leading-[1.5] text-[#2A2A3A]">
              Total processed tickets
            </span>
            {tooltipDescription && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="cursor-pointer flex" aria-label="More info">
                      <InfoIcon />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs text-tertiary font-medium">
                    {tooltipDescription}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {icon}
        </div>
        {showHeaderStats && (
          <div className="flex items-center gap-6 h-8">
            <div className="flex items-center gap-1">
              <span className="text-[28px] font-bold leading-[35px] text-ebony">
                {typeof total === 'string' ? total : formatNumber(total)}
              </span>
              {typeof totalTrend === 'number' && totalTrend !== 0 && (
                <TrendBadge value={totalTrend} />
              )}
            </div>
            <BreakdownStat
              color={COLOR_RESOLVED}
              label="Resolved"
              value={resolved}
              trend={resolvedTrend}
            />
            <BreakdownStat
              color={COLOR_ESCALATED}
              label="Escalated"
              value={escalated}
              trend={escalatedTrend}
            />
          </div>
        )}
      </div>
      <div className="w-full h-[304px]">
        {timeSeriesResponse.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center text-sm font-medium text-[#4A4A66]">
            No data for the selected time range
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={timeSeriesResponse}
              barCategoryGap="35%"
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <XAxis
                dataKey="formattedGroup"
                tickLine={false}
                axisLine={{ stroke: '#DFE3E8' }}
                tick={{
                  fontFamily: 'Bai Jamjuree',
                  fontSize: 12,
                  fontWeight: 500,
                  fill: COLOR_AXIS,
                  dy: 8,
                }}
              />
              <YAxis
                tickLine={false}
                axisLine={{ stroke: '#DFE3E8' }}
                allowDecimals={false}
                tick={{
                  fontFamily: 'Bai Jamjuree',
                  fontSize: 12,
                  fontWeight: 500,
                  fill: COLOR_AXIS,
                  dx: -4,
                }}
                width={40}
              />
              <RechartsTooltip
                content={<ChartTooltip />}
                cursor={{ fill: 'rgba(93,78,199,0.05)' }}
              />
              <Bar
                name="Resolved"
                dataKey="resolvedTickets"
                stackId="tickets"
                fill={COLOR_RESOLVED}
                // barSize={'7%'}
                maxBarSize={20}
              />
              <Bar
                name="Escalated"
                dataKey="escalatedTickets"
                stackId="tickets"
                fill={COLOR_ESCALATED}
                maxBarSize={20}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      {showLegend && (
        <div className="flex items-center justify-center gap-6 w-full">
          <LegendDot color={COLOR_RESOLVED} label="Resolved" />
          <LegendDot color={COLOR_ESCALATED} label="Escalated" />
        </div>
      )}
    </div>
  );
};

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <span className="w-2 h-2 shrink-0" style={{ backgroundColor: color }} />
    <span className="text-xs font-medium leading-[1.5] text-tertiary">{label}</span>
  </div>
);

const BreakdownStat: React.FC<{
  color: string;
  label: string;
  value: number;
  trend?: number | null;
}> = ({ color, label, value, trend }) => (
  <div className="flex items-center gap-2">
    <span className="w-2 h-2 shrink-0" style={{ backgroundColor: color }} />
    <span className="text-xs font-medium leading-[1.5] text-[#4A4A66]">{label}</span>
    <div className="flex items-center gap-1">
      <span className="text-[18px] font-bold leading-[1.5] text-ebony">{formatNumber(value)}</span>
      {typeof trend === 'number' && trend !== 0 && <TrendBadge value={trend} />}
    </div>
  </div>
);
